import { formatCurrency } from '@/lib/formatters';
import type { DiretoriaKpiTone } from '@/lib/diretoriaKpiTone';
import { formatVariacaoPct } from '@/lib/diretoriaPeriod';

export type DiretoriaExecutiveSource = 'api' | 'erp';

export interface DiretoriaExecutiveCard {
  id: string;
  label: string;
  value: string;
  sublabel?: string;
  trend?: string;
  trendPositive?: boolean;
  valueTone?: DiretoriaKpiTone;
  source: DiretoriaExecutiveSource;
  isSnapshot?: boolean;
  /** Badge sob o valor (Snapshot ou Posição no fim do período). */
  positionBadge?: string;
}

export interface DiretoriaDashboardStats {
  totalTitulos?: number;
  titulosAbertos?: number;
  titulosVencidos?: number;
  clientesInadimplentes?: number;
  valorTotal?: number;
  operadoresAtivos?: number;
  empresasAtivas?: number;
  aging1a2Dias?: { count?: number; valor?: number };
  aging3a6Dias?: { count?: number; valor?: number };
  agingAcima7Dias?: { count?: number; valor?: number };
  titulosPorEmpresa?: Array<{ empresa: string; count: number; valor: number }>;
  meta?: {
    periodApplied?: boolean;
    dataTo?: string | null;
    dataFrom?: string | null;
    dataReferencia?: string;
  };
}

export interface DiretoriaFaturamentoMesStats {
  totalPago: number;
  ano?: number;
  mes?: number;
  periodo?: { dataFrom: string; dataTo: string };
  anteriorTotal?: number;
  variacaoPct?: number | null;
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

export interface DiretoriaReceitaProjetadaStats {
  totalAReceber: number;
  competencias: Array<{ ano: number; mes: number; competencia: string; totalAReceber: number }>;
  periodo?: { dataFrom: string; dataTo: string };
  anteriorTotal?: number;
  variacaoPct?: number | null;
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

function formatMonthYear(mes?: number, ano?: number): string {
  if (!mes || !ano) return 'ERP FlyStart';
  return `${String(mes).padStart(2, '0')}/${ano}`;
}

function buildTrend(
  variacaoPct: number | null | undefined,
  compareLabel?: string
): Pick<DiretoriaExecutiveCard, 'trend' | 'trendPositive'> {
  if (variacaoPct == null || !Number.isFinite(variacaoPct)) return {};
  const vs = compareLabel ? `vs ${compareLabel}` : 'vs período comparativo';
  return {
    trend: `${formatVariacaoPct(variacaoPct)} ${vs}`,
    trendPositive: variacaoPct >= 0,
  };
}

function formatDataToPt(dataTo?: string | null): string | null {
  if (!dataTo) return null;
  const m = dataTo.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dataTo;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

const COBRANCA_SNAPSHOT_SUB = 'Situação atual · não filtrado por período';

function cobrancaPositionSublabel(
  dashboard: DiretoriaDashboardStats | null | undefined,
  periodLabel?: string
): { sublabel: string; isSnapshot: boolean; badgeLabel?: string } {
  if (dashboard == null) return { sublabel: 'Cobrança', isSnapshot: false };
  const periodApplied = Boolean(dashboard.meta?.periodApplied);
  const dataToPt = formatDataToPt(dashboard.meta?.dataTo);
  if (periodApplied && (dataToPt || periodLabel)) {
    const pos = dataToPt ? `Posição em ${dataToPt}` : 'Posição no fim do período';
    return {
      sublabel: periodLabel ? `${pos} · ${periodLabel}` : pos,
      isSnapshot: false,
      badgeLabel: 'Posição no fim do período',
    };
  }
  return {
    sublabel: COBRANCA_SNAPSHOT_SUB,
    isSnapshot: true,
    badgeLabel: 'Snapshot',
  };
}

export function buildExecutiveCards(
  dashboard: DiretoriaDashboardStats | null | undefined,
  faturamento: DiretoriaFaturamentoMesStats | null | undefined,
  receitaProjetada: DiretoriaReceitaProjetadaStats | null | undefined,
  periodLabel?: string,
  compareLabel?: string
): DiretoriaExecutiveCard[] {
  const valorCarteira = Number(dashboard?.valorTotal ?? 0);
  const titulosVencidos = Number(dashboard?.titulosVencidos ?? 0);
  const clientesInad = Number(dashboard?.clientesInadimplentes ?? 0);
  const agingLeve = Number(dashboard?.aging1a2Dias?.count ?? 0);
  const faturamentoPeriodo = Boolean(faturamento?.periodo || periodLabel);
  const cobranca = cobrancaPositionSublabel(dashboard, periodLabel);

  return [
    {
      id: 'exec-faturamento-mes',
      label: faturamentoPeriodo ? 'Faturamento (período)' : 'Faturamento (mês)',
      value: faturamento != null ? formatCurrency(Number(faturamento.totalPago ?? 0)) : '—',
      sublabel: faturamento
        ? faturamentoPeriodo && periodLabel
          ? `${periodLabel} · ERP FlyStart`
          : `${formatMonthYear(faturamento.mes, faturamento.ano)} · ERP FlyStart`
        : 'ERP FlyStart',
      valueTone: faturamento && faturamento.totalPago > 0 ? 'good' : 'neutral',
      source: 'erp',
      ...buildTrend(faturamento?.variacaoPct, compareLabel),
    },
    {
      id: 'exec-valor-carteira',
      label: 'Valor em carteira',
      value: dashboard != null ? formatCurrency(valorCarteira) : '—',
      sublabel: cobranca.sublabel,
      valueTone: valorCarteira > 0 ? 'bad' : 'neutral',
      source: 'api',
      isSnapshot: cobranca.isSnapshot,
      positionBadge: cobranca.badgeLabel,
    },
    {
      id: 'exec-titulos-vencidos',
      label: 'Títulos vencidos',
      value: dashboard != null ? String(titulosVencidos) : '—',
      sublabel: cobranca.sublabel,
      valueTone: titulosVencidos === 0 ? 'good' : 'bad',
      source: 'api',
      isSnapshot: cobranca.isSnapshot,
      positionBadge: cobranca.badgeLabel,
    },
    {
      id: 'exec-clientes-inad',
      label: 'Clientes inadimplentes',
      value: dashboard != null ? String(clientesInad) : '—',
      sublabel: cobranca.sublabel,
      valueTone: clientesInad === 0 ? 'good' : 'bad',
      source: 'api',
      isSnapshot: cobranca.isSnapshot,
      positionBadge: cobranca.badgeLabel,
    },
    {
      id: 'exec-aging-leve',
      label: 'Vencidos 1–2 dias',
      value: dashboard != null ? String(agingLeve) : '—',
      sublabel: cobranca.sublabel,
      valueTone: agingLeve === 0 ? 'good' : 'bad',
      source: 'api',
      isSnapshot: cobranca.isSnapshot,
      positionBadge: cobranca.badgeLabel,
    },
    {
      id: 'exec-receita-projetada',
      label: 'Receita projetada (anual)',
      value: receitaProjetada != null ? formatCurrency(Number(receitaProjetada.totalAReceber ?? 0)) : '—',
      sublabel: receitaProjetada != null ? 'Período anual · ERP FlyStart' : 'ERP FlyStart',
      valueTone: 'neutral',
      source: 'erp',
      // Sem datas do filtro/comparativo — a métrica é anual e as datas confundem.
      ...buildTrend(receitaProjetada?.variacaoPct),
    },
  ];
}

/** Card Inadimplência = aging de cobrança (faixas 1–2 / 3–6 / 7+). */
export function buildInadimplenciaIndicatorFromCobranca(
  base: {
    id: string;
    title: string;
    shortDescription: string;
    primaryKpiIndex?: number;
    kpis: Array<{ label: string; value: string; hint?: string; tone?: DiretoriaKpiTone }>;
    chartKind: string;
    chartTitle?: string;
    seriesLabels?: { valor: string; valor2?: string };
    tableHeaders: string[];
    footnote: string;
    dataSource: string;
  },
  stats: DiretoriaDashboardStats,
  formatCurrencyFn: (value: number) => string = formatCurrency
) {
  const faixas = [
    {
      name: '1–2 dias',
      qtd: Number(stats.aging1a2Dias?.count ?? 0),
      saldo: Number(stats.aging1a2Dias?.valor ?? 0),
    },
    {
      name: '3–6 dias',
      qtd: Number(stats.aging3a6Dias?.count ?? 0),
      saldo: Number(stats.aging3a6Dias?.valor ?? 0),
    },
    {
      name: '7+ dias',
      qtd: Number(stats.agingAcima7Dias?.count ?? 0),
      saldo: Number(stats.agingAcima7Dias?.valor ?? 0),
    },
  ];
  const saldoAging = faixas.reduce((s, f) => s + f.saldo, 0);
  const qtdAging = faixas.reduce((s, f) => s + f.qtd, 0);
  const saldoVencido = Number(stats.valorTotal ?? saldoAging);
  const titulosVencidos = Number(stats.titulosVencidos ?? qtdAging);
  const critico7 = faixas[2]!;

  return {
    ...base,
    shortDescription:
      'Títulos VENCIDO/PARCIAL com statusLocacao APROVADA — aging por faixa de atraso.',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Saldo vencido',
        value: formatCurrencyFn(saldoVencido),
        hint: 'Cobrança · as-of dataTo',
        tone: (saldoVencido > 0 ? 'bad' : 'good') as DiretoriaKpiTone,
      },
      {
        label: 'Títulos vencidos',
        value: String(titulosVencidos),
        hint: 'Cobrança · as-of dataTo',
        tone: (titulosVencidos > 0 ? 'bad' : 'good') as DiretoriaKpiTone,
      },
      {
        label: 'Em 7+ dias',
        value: `${critico7.qtd} · ${formatCurrencyFn(critico7.saldo)}`,
        hint: 'Faixa crítica do aging',
        tone: (critico7.qtd > 0 ? 'bad' : 'good') as DiretoriaKpiTone,
      },
    ],
    chartKind: 'barGrouped' as const,
    chartTitle: 'Títulos a receber vencidos por faixa',
    series: faixas.map((f) => ({
      name: f.name,
      valor: f.qtd,
      valor2: Math.round(f.saldo),
    })),
    seriesLabels: { valor: 'Qtd.', valor2: 'Saldo' },
    tableHeaders: ['Faixa de atraso', 'Qtd.', 'Saldo'],
    tableRows: faixas.map((f) => [f.name, String(f.qtd), formatCurrencyFn(f.saldo)]),
    footnote:
      'Cobrança (MySQL): statusLocacao=APROVADA; status IN (VENCIDO, PARCIAL); vencimento < dataTo; exclui multas. Faixas 1–2 / 3–6 / 7+ dias (civil).',
    dataSource: 'cobranca' as const,
    hasData: true,
  };
}

export const DIRETORIA_SNAPSHOT_INDICATOR_IDS = new Set([
  'motos-disponiveis',
  'motos-status',
]);
